import { IoPersonOutline } from 'react-icons/io5';
import { Link } from 'common/components/Link/animated';
import { IBlogItemOverview } from 'common/types/blog';
import { formatDate } from 'common/utils/date';
import { getEmployeeProfileHref } from 'common/utils/employeeProfile';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

export const BlogItemLatest: React.FC<{ blog: IBlogItemOverview }> = ({ blog }) => {
  const { i18n } = useTranslation();
  const { author, date, excerpt, tags, title, slug, image } = blog;
  const blogHref = typeof slug === 'string' && slug.length > 0 ? slug : null;

  const renderAuthorImage = () => {
    if (author.image.src) {
      return (
        <Image
          alt={author.image.alt}
          className="mr-2 h-[30px] w-[30px] rounded-full bg-gray-800 text-white"
          src={author.image.src}
          height={author.image.height}
          width={author.image.width}
        />
      );
    }
    return (
      <div className="mr-2 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-primary/10">
        <IoPersonOutline color="white" size={14} />
      </div>
    );
  };

  const renderAuthor = () => {
    if (author.name === 'Ivan Vukušić') {
      const employeeHref = getEmployeeProfileHref(author.name, i18n.language);
      return (
        <Link href={employeeHref} className="flex items-center text-white">
          {renderAuthorImage()}
          <div>{author.name}</div>
        </Link>
      );
    }
    return (
      <div className="flex items-center text-white">
        {renderAuthorImage()}
        <div>{author.name}</div>
      </div>
    );
  };

  const classNameTag =
    'bg-white/10 p-3 py-2 text-[16px] hover:border-primary hover:text-primary p-2 py-1 mr-1 mb-1 hover:border-primary hover:text-primary cursor-pointer caret-white text-white';
  return (
    <article
      key={slug}
      className="flex h-full flex-col justify-between border-[1px] border-white/10 bg-[#261434] pb-4">
      <div>
        {blogHref ? (
          <Link href={blogHref}>
            <div className="group relative min-h-[200px] overflow-hidden lg:h-[260px]">
              <Image
                src={image.src}
                className="w-full object-contain object-center"
                alt={image.alt}
                height={image.height}
                width={image.width}
              />
              <div className="absolute bottom-0 left-[50%] top-0 w-0 bg-[#261434] opacity-0 transition-all duration-500 group-hover:w-[100%] group-hover:-translate-x-[50%] group-hover:opacity-50" />
            </div>
          </Link>
        ) : (
          <div className="group relative min-h-[200px] overflow-hidden lg:h-[260px]">
            <Image
              src={image.src}
              className="w-full object-contain object-center"
              alt={image.alt}
              height={image.height}
              width={image.width}
            />
          </div>
        )}
        <div className="px-4">
          <div className="mt-3">
            <div className="mt-1 flex flex-col text-white">
              {renderAuthor()}
              <div className="flex flex-row items-center text-[16px] leading-4 text-white pl-[36px]">
                {formatDate(date, 'DD MMMM YYYY', i18n.language)}
              </div>
            </div>
          </div>
          {blogHref ? (
            <Link href={blogHref}>
              <h2 className="text-heading-item mb-1 max-w-xl pt-4 font-bold text-white">{title}</h2>
            </Link>
          ) : (
            <h2 className="text-heading-item mb-1 max-w-xl pt-4 font-bold text-white">{title}</h2>
          )}
          <div className="mb-3 max-w-2xl pt-2 text-white/90">{excerpt}</div>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap px-4">
        {tags.map((tag: { href?: string; label: string; slug: string }) => {
          if (tag.href) {
            return (
              <Link className={classNameTag} href={tag.href} key={tag.slug}>
                {tag.label}
              </Link>
            );
          }
          return (
            <div className={classNameTag} key={tag.slug}>
              {tag.label}
            </div>
          );
        })}
      </div>
    </article>
  );
};
